import {
  Box,
  Divider,
  Drawer,
  Icon,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled
} from "@mui/material";
import {
  PeopleAlt,
  GridView,
  Quiz,
  Logout,
  Forest,
  Construction,
  Assessment,
  BarChart
} from "@mui/icons-material";
import { onLogout } from "../data/auth";
import { ReactNode } from "preact/compat";
import { Wordmark } from "../components/Wordmark";
import { useLocation } from "preact-iso";

const DRAWER_WIDTH = 160;

const TOP_NAV = [
  { text: "Audiences", target: "/audiences", icon: PeopleAlt },
  { text: "Turf", target: "/turf", icon: GridView },
  { text: "Surveys", target: "/surveys", icon: Quiz },
  { text: "Launches", target: "/launches", icon: Forest },
  { text: "Progress", target: "/progress", icon: Assessment },
  { text: "Reports", target: "/reports", icon: Construction }
];

const BOTTOM_NAV = [{ text: "Metrics", target: "/metrics", icon: BarChart }];

// `/` renders Audiences, so treat root + `/audiences/*` as the Audiences tab
const isActive = (path: string, target: string) =>
  target === "/audiences"
    ? path === "/" || path.startsWith("/audiences")
    : path.startsWith(target);

const NavButton = styled(ListItemButton)(({ theme }) => ({
  borderLeft: "4px solid transparent",
  "&.Mui-selected": {
    borderLeftColor: theme.palette.secondary.main,
    backgroundColor: "transparent",
    "& .MuiListItemIcon-root": { color: theme.palette.secondary.main },
    "& .MuiListItemText-primary": { color: theme.palette.secondary.main }
  }
}));

const NavText = styled(ListItemText)(({ theme }) => ({
  "& .MuiListItemText-primary": theme.typography.subtitle2
}));

const NavItem = ({
  text,
  target,
  icon,
  selected
}: {
  text: string;
  target: string;
  icon: any;
  selected: boolean;
}) => {
  return (
    <ListItem key={target} disablePadding sx={{ display: "block" }}>
      <NavButton selected={selected} component="a" href={target}>
        <ListItemIcon>
          <Icon component={icon} />
        </ListItemIcon>
        <NavText primary={text} />
      </NavButton>
    </ListItem>
  );
};

export function Layout({ children }: { children: ReactNode }) {
  const { path } = useLocation();
  return (
    <Box sx={{ display: "flex" }}>
      <Drawer
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box"
          }
        }}
        variant="permanent"
        anchor="left"
      >
        <Box sx={{ px: 2, py: 2 }}>
          <Wordmark />
        </Box>
        <Divider />
        <List>
          {TOP_NAV.map((p) => (
            <NavItem
              selected={isActive(path, p.target)}
              key={p.target}
              {...p}
            />
          ))}
        </List>
        <Box sx={{ flexGrow: 1 }} />
        <Divider flexItem />
        <List>
          {BOTTOM_NAV.map((p) => (
            <NavItem
              selected={isActive(path, p.target)}
              key={p.target}
              {...p}
            />
          ))}
          <ListItem key="logout" disablePadding>
            <NavButton onClick={onLogout}>
              <ListItemIcon>
                <Logout />
              </ListItemIcon>
              <NavText primary="Logout" />
            </NavButton>
          </ListItem>
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: "100vh",
          overflow: "auto",
          bgcolor: "background.default",
          p: 3
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
